/**
 * The profile schema: everything a deploy's game contains, as plain data.
 *
 * A profile is config, not code — it arrives as JSON, so nothing in here may assume it
 * was produced by a TypeScript author. `parse.ts` turns unknown JSON into these types,
 * `validate.ts` checks that a structurally sound profile also makes sense, and
 * `buildGeometry.ts` derives the walls. No three.js, no Rapier, no React.
 *
 * Everything spatial is in metres, in a right-handed Y-up world. Room floors all sit at
 * y = 0 — the house is single-storey by construction.
 */

export type Vec3 = { x: number; y: number; z: number };

/** A point on the ground plane. */
export type GroundPoint = { x: number; z: number };

/** One straight leg of the trodden path through a maze's grounds, on the ground plane. */
export type PathSegment = { from: GroundPoint; to: GroundPoint };

/** An axis-aligned box, positioned by its centre. Sizes are full extents. */
export type Box = {
  center: Vec3;
  size: Vec3;
};

/** An axis-aligned footprint on the ground plane. `x`/`z` is the minimum corner. */
export type Rect = {
  x: number;
  z: number;
  width: number;
  depth: number;
};

export type Spawn = {
  /** Where the player's feet start. */
  position: Vec3;
  /** Facing, in radians, counter-clockwise from -Z. */
  yaw: number;
};

/**
 * `'hedge'` is derived, never authored: it is what the grounds of a maze level are
 * drawn with. A profile may only name the three indoor floors.
 */
export type FloorPattern = 'wood' | 'tile' | 'concrete' | 'hedge';
export type WallPattern = 'wallpaper' | 'paint';

/**
 * Colors are CSS strings — the renderer feeds them to materials as-is. Patterns name
 * the procedural texture drawn in that color; omitted means the profile default
 * (wood floors, wallpapered walls).
 */
export type RoomPalette = {
  floor: string;
  walls: string;
  ceiling: string;
  floorPattern?: FloorPattern;
  wallPattern?: WallPattern;
};

export type Palette = {
  /** Colors for rooms that do not override their own. */
  room: RoomPalette;
  door: string;
  box: string;
  zone: string;
  /**
   * Skirting, cornice, casings and window frames. One colour for the whole house on
   * purpose: real trim is painted the same throughout, and it is what ties rooms of
   * clashing wall colours together. Omitted means `DEFAULT_TRIM_COLOR`.
   */
  trim?: string;
};

/**
 * A room's interior light. Every field is optional: a room that says nothing gets a warm
 * light hanging over its middle, which is what most rooms want. Authoring exists for the
 * ones that do not — a dim storeroom, a lamp in a corner rather than a fitting overhead.
 */
export type RoomLightSpec = {
  color?: string;
  intensity?: number;
  /** Metres below the ceiling the light hangs. */
  drop?: number;
  /** Displacement from the middle of the room, on the ground plane. */
  offset?: GroundPoint;
};

/**
 * Which of a room's four walls something sits on, named by the side of the room it is
 * on. `west`/`east` are the walls at minimum and maximum x; `north`/`south` at minimum
 * and maximum z. Naming them beats naming an axis and a line, which an author would
 * have to work out from the rect every time.
 */
export type WallSide = 'west' | 'east' | 'north' | 'south';

/**
 * A window in one of a room's exterior walls. `offset` is measured along the wall from
 * the room's minimum corner — from `rect.z` on a west or east wall, from `rect.x` on a
 * north or south one. `sill` and `head` are heights above the floor.
 */
export type WindowSpec = {
  id: string;
  wall: WallSide;
  offset: number;
  width: number;
  sill: number;
  head: number;
};

/**
 * Where a room's light switch goes. Omitted means one is placed automatically — a room
 * with no way to restore its own light would be a trap, so this is never optional in
 * effect, only in authoring.
 */
export type LightSwitchSpec = {
  wall: WallSide;
  /** Along the wall from the room's minimum corner, as for a window. */
  offset: number;
};

export type RoomSpec = {
  id: string;
  rect: Rect;
  palette?: Partial<RoomPalette>;
  light?: RoomLightSpec;
  lightSwitch?: LightSwitchSpec;
  windows?: readonly WindowSpec[];
};

/** The span of a room's wall on a given side: the axis it runs along, and its extent. */
export function wallSpan(rect: Rect, side: WallSide): { axis: 'x' | 'z'; line: number; from: number; to: number } {
  switch (side) {
    case 'west':
      return { axis: 'x', line: rect.x, from: rect.z, to: rect.z + rect.depth };
    case 'east':
      return { axis: 'x', line: rect.x + rect.width, from: rect.z, to: rect.z + rect.depth };
    case 'north':
      return { axis: 'z', line: rect.z, from: rect.x, to: rect.x + rect.width };
    case 'south':
      return { axis: 'z', line: rect.z + rect.depth, from: rect.x, to: rect.x + rect.width };
  }
}

export type DoorSpec = {
  id: string;
  /** The two rooms the door connects. Their rects must share a boundary. */
  betweenRooms: readonly [string, string];
  /** Centre of the opening, on the shared boundary line. */
  center: GroundPoint;
  width: number;
  height: number;
  isInitiallyOpen?: boolean;
};

/**
 * A closed union on purpose: later changes (keys, furniture) extend it here.
 *
 * `robot-dog-body` and `robot-dog-leg` are two kinds rather than one `robot-dog-part`
 * with a variant field (design D1): a variant axis inside a kind would make every
 * `OBJECT_KINDS` consumer learn about it, where two kinds cost nothing.
 */
export type ObjectKind = 'cardboard-box' | 'screwdriver' | 'robot-dog-body' | 'robot-dog-leg';

export type ObjectSpec = {
  id: string;
  kind: ObjectKind;
  roomId: string;
  /** Where the object rests on the floor. */
  position: GroundPoint;
  /** Full extents. Omitted means the kind's default size. */
  size?: Vec3;
};

export type ObjectiveKind = 'deposit-objects' | 'reach-exit';

/** Carry the named objects into a marked patch of one room's floor. */
export type DepositObjectsObjective = {
  id: string;
  kind: 'deposit-objects';
  /** Shown to the player in the HUD. */
  description: string;
  /** The objects that must end up in the zone. */
  objectIds: readonly string[];
  zone: {
    roomId: string;
    rect: Rect;
  };
};

/**
 * Walk out of the maze. There is nothing to author: the zone is the exit the maze
 * generator opened, and only a grounds level has one.
 */
export type ReachExitObjective = {
  id: string;
  kind: 'reach-exit';
  description: string;
};

export type ObjectiveSpec = DepositObjectsObjective | ReachExitObjective;

/**
 * Outdoor maze grounds: a seeded square labyrinth on grass, ringed by a moat. A level
 * declares `grounds` or `rooms`, never both — see `validate.ts`.
 */
export type GroundsSpec = {
  seed: string;
  /** Maze cells across (x). */
  columns: number;
  /** Maze cells deep (z). */
  rows: number;
  /** Defaults to DEFAULT_HEDGE_HEIGHT: tall enough to hide the maze, low enough to
   * glimpse over at the apex of a jump. */
  hedgeHeight?: number;
  /** Defaults to DEFAULT_MOAT_WIDTH. */
  moatWidth?: number;
  /**
   * Whether the entry apron is walled into a courtyard around the spawn, so the player starts
   * inside the labyrinth with the maze's entry gap as the only way out. Defaults to false: a
   * level starts on the open apron unless it asks not to. The yard follows the seed — it is
   * centred on whichever column the entry gap fell in — so the spawn must stand inside it, and
   * validation says so rather than starting the player behind a wall.
   */
  startCourtyard?: boolean;
  /**
   * How many ladders stand against the hedges, up to one per cell. Defaults to 0: a maze
   * has none unless its profile asks for them. Where they stand comes from `seed`.
   */
  ladderCount?: number;
  /**
   * How many tables stand in the maze's dead ends, up to one per dead end. Defaults to 0.
   * Which cul-de-sacs get one comes from `seed`.
   */
  tableCount?: number;
  /**
   * Small items left lying on the tables, one per table, in this order. Defaults to none.
   * Which tables get one comes from `seed`. Every kind must be stowable and there must be
   * at least as many tables as items — validation says so rather than placing fewer.
   */
  tableItems?: readonly ObjectKind[];
};

export type LevelSpec = {
  id: string;
  spawn: {
    position: GroundPoint;
    yaw: number;
  };
  /** Wall (and ceiling) height. Defaults to DEFAULT_CEILING_HEIGHT. */
  ceilingHeight?: number;
  rooms: readonly RoomSpec[];
  doors: readonly DoorSpec[];
  objects: readonly ObjectSpec[];
  objectives: readonly ObjectiveSpec[];
  /** Low authored boxes the player can step onto (thresholds, low platforms). */
  steps?: readonly Box[];
  /** An outdoor level's maze grounds, in place of rooms. */
  grounds?: GroundsSpec;
  /**
   * The id of the level this level's exit leads to. Absent means the run ends here — the
   * exit completes the objective and finishes the game. Which gate stands where, and
   * where the player arrives, are derived from this and from the maze; see
   * `src/game/progression.ts`.
   */
  exitsTo?: string;
};

/**
 * A profile's declaration that its run continues, without end, into generated mazes.
 * `minCells`..`maxCells` bounds both the columns and the rows of each one.
 */
export type EndlessSpec = {
  seed: string;
  minCells: number;
  maxCells: number;
  /** The wording of the objective every generated maze carries. */
  objective: string;
};

export type Profile = {
  id: string;
  /**
   * What this game is called — the name it installs under on a home screen, and the
   * page title. One repo builds several games, so the name is content like the maze is;
   * for a deployed game it matches the subdomain it is served from, which is the name
   * its players know it by. See `scripts/profileAppName.ts`.
   */
  name: string;
  /**
   * A shorter label for where the full name will not fit — iOS truncates a home screen
   * caption at around a dozen characters. Absent means the name is short enough to be
   * used as it is.
   */
  shortName?: string;
  palette: Palette;
  levels: readonly LevelSpec[];
  /**
   * Whether this game gives the player a pocket. Absent means it does — the field is an
   * opt-out for a game with nothing worth carrying two of, not something every profile
   * has to answer. It belongs to the profile rather than to a level so that it cannot
   * change part way through a run.
   */
  pocket?: boolean;
  /** Absent means the run ends at its last level. */
  endless?: EndlessSpec;
};

export function boxMin(box: Box): Vec3 {
  return {
    x: box.center.x - box.size.x / 2,
    y: box.center.y - box.size.y / 2,
    z: box.center.z - box.size.z / 2,
  };
}

export function boxMax(box: Box): Vec3 {
  return {
    x: box.center.x + box.size.x / 2,
    y: box.center.y + box.size.y / 2,
    z: box.center.z + box.size.z / 2,
  };
}

/** The walkable height of a box's top face. */
export function boxTop(box: Box): number {
  return box.center.y + box.size.y / 2;
}

export function rectContains(rect: Rect, point: GroundPoint): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.z >= rect.z &&
    point.z <= rect.z + rect.depth
  );
}

/**
 * The boundary segment two room rects share, if they are flush against each other.
 * `axis` is the axis the boundary line is perpendicular to: `'x'` means the rooms meet
 * on a line of constant x and the shared wall runs along z, spanning `min..max`.
 */
export type SharedBoundary = { axis: 'x' | 'z'; line: number; min: number; max: number };

const ADJACENCY_EPSILON = 1e-6;

export function sharedBoundary(a: Rect, b: Rect): SharedBoundary | null {
  const flush = (edgeA: number, edgeB: number) => Math.abs(edgeA - edgeB) < ADJACENCY_EPSILON;

  if (flush(a.x + a.width, b.x) || flush(b.x + b.width, a.x)) {
    const line = flush(a.x + a.width, b.x) ? a.x + a.width : a.x;
    const min = Math.max(a.z, b.z);
    const max = Math.min(a.z + a.depth, b.z + b.depth);
    if (max - min > ADJACENCY_EPSILON) return { axis: 'x', line, min, max };
  }

  if (flush(a.z + a.depth, b.z) || flush(b.z + b.depth, a.z)) {
    const line = flush(a.z + a.depth, b.z) ? a.z + a.depth : a.z;
    const min = Math.max(a.x, b.x);
    const max = Math.min(a.x + a.width, b.x + b.width);
    if (max - min > ADJACENCY_EPSILON) return { axis: 'z', line, min, max };
  }

  return null;
}

export function rectContainsRect(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.z >= outer.z &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.z + inner.depth <= outer.z + outer.depth
  );
}
